import { useTranslation } from "react-i18next";
import { MobileCardRow, MobileCardWrapper, TableBody, TableColumn, TableHeader, TableRow, TableCell } from "./dashboard/table-components";
import { SearchIcon, EditIcon, DeleteIcon, ArrowLeftIcon, ArrowRightIcon } from "../assets/dashboard-icon.svg";
import { useRef, useState, useEffect, useCallback } from "react";
import { fetchCustomers, Customer, CustomerResponse } from "../api/customer";
import { useHolidayRecords } from "../hooks/useHolidayPay";
import { HolidayRecord } from "../api/holidaypayreport";
import { formatAmount, formatDateForPayout, getStatusBadgeClass } from "../helper/common.helper";
import { HolidayPaySidebar } from "./holiday-pay-sidebar.component";
import { HolidayPayoutModal } from "./holiday-pay-payout-modal.component";
import { getItem } from "../helper/localstorage.helper";
import axios from "axios";
import { toast } from "react-toastify";
import CalendarSingle from "./calendar/calendar-single.component";
import "./holiday-pay-calendar.scss";
import { HolidayInvoiceHistoryModal } from "./holiday-pay-invoice-history.component";
import LoadingCard from '../components/SkeletonLoading'

export const HolidayPayReportSection = ({
    className,
}: {
    className?: string
}
) => {
    const { t } = useTranslation();
    const fromCalendarRef = useRef<HTMLDivElement>(null);
    const toCalendarRef = useRef<HTMLDivElement>(null);
    const isMobile = window.innerWidth <= 768;
    const [showSidebar, setShowSidebar] = useState<boolean>(true);
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [showFromCalendar, setShowFromCalendar] = useState<boolean>(false);
    const [showToCalendar, setShowToCalendar] = useState<boolean>(false);
    const [showPayoutModal, setShowPayoutModal] = useState<boolean>(false);
    const [payoutLoading, setPayoutLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [appliedFilters, setAppliedFilters] = useState({
        fromDate: '',
        toDate: '',
        status: ''
    });
    const [activeTab, setActiveTab] = useState('transaction-history-label');

    const tabs = [
        { key: 'transaction-history-label', label: t('TRANSACTION_HISTORY') },
        { key: 'payout-history', label: t('PAYOUT_HISTORY_LABEL') },
    ];

    const [toolTipError, setToolTipError] = useState({
        invalidFromdate: false,
        toDateNotSelected: false,
        fromDateNotSelected: false,
    });
    const {
        calculatedBalance,
        payoutAmount,
        fetchData,
        getHolidayAmount,
        paginatedHolidayRecords,
        totalPages,
        currentPage,
        searchQuery,
        setCurrentPage,
        setSearchQuery,
        goToPreviousPage,
        goToNextPage,
        loading
    } = useHolidayRecords(10);
    const language = getItem("i18nextLng") || 'en-us';

    useEffect(() => {
        fetchData(currentPage, fromDate, toDate, status);
    }, [fetchData, currentPage, fromDate, toDate, status]);

    // Initial fetch of holiday amount on component mount
    useEffect(() => {
        getHolidayAmount();
    }, [getHolidayAmount]);

    // Debounce the filter changes to avoid too many API calls
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (currentPage !== 1) {
                setCurrentPage(1);
            } else {
                fetchData(1, fromDate, toDate, status);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [fromDate, toDate, status, fetchData]);

    useEffect(() => {
        if (toolTipError.invalidFromdate || toolTipError.toDateNotSelected || toolTipError.fromDateNotSelected) {
            const timer = setTimeout(() => {
                setToolTipError({
                    invalidFromdate: false,
                    toDateNotSelected: false,
                    fromDateNotSelected: false,
                });
            }, 6000);

            return () => clearTimeout(timer);
        }
    }, [toolTipError]);


    const handleFromDateSelect = (date: string) => {
        if (!date) {
            setToolTipError(prev => ({ ...prev, fromDateNotSelected: true }));
            return;
        }

        setFromDate(date);
        setShowFromCalendar(false);
        setToolTipError({
            invalidFromdate: false,
            toDateNotSelected: false,
            fromDateNotSelected: false,
        });

        // Call getHolidayAmount when both dates are available
        if (date && toDate) {
            getHolidayAmount(date, toDate);
        }
    };

    const handleToDateSelect = (date: string) => {
        if (!date) {
            setToolTipError(prev => ({ ...prev, toDateNotSelected: true }));
            return;
        }

        setToDate(date);
        setShowToCalendar(false);
        setToolTipError({
            invalidFromdate: false,
            toDateNotSelected: false,
            fromDateNotSelected: false,
        });

        // Call getHolidayAmount when both dates are available
        if (fromDate && date) {
            getHolidayAmount(fromDate, date);
        }
    };


    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setStatus(e.target.value);
    };

    const handleClearFilters = () => {
        try {
            setFromDate('');
            setToDate('');
            setStatus('');
            setAppliedFilters({ fromDate: '', toDate: '', status: '' });
            setCurrentPage(1);
            setToolTipError({
                invalidFromdate: false,
                toDateNotSelected: false,
                fromDateNotSelected: false,
            });
        } catch (error) {
            console.error('Error clearing filters:', error);
            toast.error('Error clearing filters');
        }
    };


    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (
                let i = Math.max(2, currentPage - 1);
                i <= Math.min(totalPages - 1, currentPage + 1);
                i++
            ) {
                if (!pages.includes(i)) pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };
    return (
        <div className={`holiday-pay-layout ${className || ''}`}>

            <div className="holiday-pay-layout-main">
                <div className="holiday-pay-card-header">
                    <div className="holiday-pay-header-content">
                        <div className="holiday-pay-title-section">
                            <h2 className="holiday-pay-main-title">{t('PAY_OUT_YOUR_OLD_HOLIDAY_PAY')}</h2>
                            <p className="holiday-pay-description">{t('SEE_YOUR_AVAILABLE_HOLIDAY_PAY_AND_REQUEST_A_PAYOUT_ANYTIME')}</p>
                        </div>
                        <button className="pay_out btn btn-primary" onClick={() => setShowPayoutModal(true)}>
                            {t('PAY_OUT')}
                        </button>
                    </div>
                </div>
                <div className='px-3 d-flex' style={{ gap: '24px' }}>
                    {tabs.map(({ key, label }) => (
                        <button
                            key={key}
                            className={`content-tab-btn  ${activeTab.includes(key) ? 'active ' : ''}`}
                            onClick={() => setActiveTab(key)}
                        >
                            <h1 className="dashboard-page__title mb-0 py-3">
                                {label}
                            </h1>
                        </button>
                    ))}
                </div>
                {activeTab === 'payout-history' && (
                    <div className="invoice-table-container">
                        <div className="invoice-table-header">
                            <h3 className="invoice-table-title">{t('YOUR_HOLIDAY_SAVINGS_PAYOUT')}</h3>
                            <div className="holiday-filter-section">
                                <div className="holiday-filter-inputs">
                                    <div className="holiday-filter-input" ref={fromCalendarRef}>
                                        <div className="holiday-filter-input-box" onClick={() => setShowFromCalendar(!showFromCalendar)}>
                                            <span className="holiday-filter-label">
                                                {fromDate ? new Date(fromDate).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                }) : t('From')}
                                            </span>
                                            {toolTipError.fromDateNotSelected && (
                                                <div className="tooltip-error">
                                                    {t('PLEASE_SELECT_FROM_DATE')}
                                                </div>
                                            )}
                                            {toolTipError.invalidFromdate && (
                                                <div className="tooltip-error">
                                                    {t('FROM_DATE_CANNOT_BE_GREATER_THAN_TO_DATE')}
                                                </div>
                                            )}
                                            <div className="holiday-filter-icon">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                    <path d="M8 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                                    <path d="M16 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                                    <rect x="3" y="4" width="18" height="15" rx="2" stroke="#68727d" strokeWidth="1.5" />
                                                    <path d="M3 10H21" stroke="#68727d" strokeWidth="1.5" />
                                                    <path d="M8 14H8.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                    <path d="M12 14H12.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                    <path d="M16 14H16.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                    <path d="M8 17H8.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                    <path d="M12 17H12.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                    <path d="M16 17H16.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        {showFromCalendar && (
                                            <div className="calendar-dropdown">
                                                <div className="calendar-dropdown-header">
                                                    <span>{t('SELECT_FROM_DATE')}</span>
                                                    <button
                                                        type="button"
                                                        className="calendar-dropdown-close"
                                                        onClick={() => setShowFromCalendar(false)}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                                <CalendarSingle
                                                    onDateSelect={(date) => {
                                                        const formattedDate = date.toISOString().split('T')[0];
                                                        handleFromDateSelect(formattedDate);
                                                        setShowFromCalendar(false);
                                                    }}
                                                    maxYear={new Date().getFullYear()}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="holiday-filter-input" ref={toCalendarRef}>
                                        <div className="holiday-filter-input-box" onClick={() => setShowToCalendar(!showToCalendar)}>
                                            <span className="holiday-filter-label">
                                                {toDate ? new Date(toDate).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                }) : t('To')}
                                            </span>
                                            {toolTipError.toDateNotSelected && (
                                                <div className="tooltip-error">
                                                    {t('PLEASE_SELECT_TO_DATE')}
                                                </div>
                                            )}
                                            <div className="holiday-filter-icon">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                    <path d="M8 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                                    <path d="M16 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                                    <rect x="3" y="4" width="18" height="15" rx="2" stroke="#68727d" strokeWidth="1.5" />
                                                    <path d="M3 10H21" stroke="#68727d" strokeWidth="1.5" />
                                                    <path d="M8 14H8.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                    <path d="M12 14H12.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                    <path d="M16 14H16.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                    <path d="M8 17H8.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                    <path d="M12 17H12.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                    <path d="M16 17H16.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                </svg>
                                            </div>
                                        </div>
                                        {showToCalendar && (
                                            <div className="calendar-dropdown">
                                                <div className="calendar-dropdown-header">
                                                    <span>{t('SELECT_TO_DATE')}</span>
                                                    <button
                                                        type="button"
                                                        className="calendar-dropdown-close"
                                                        onClick={() => setShowToCalendar(false)}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                                <CalendarSingle
                                                    onDateSelect={(date) => {
                                                        const formattedDate = date.toISOString().split('T')[0];
                                                        handleToDateSelect(formattedDate);
                                                        setShowToCalendar(false);
                                                    }}
                                                    maxYear={new Date().getFullYear()}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="holiday-filter-input">
                                        <div className="holiday-filter-input-box">
                                            <select
                                                value={status}
                                                onChange={handleStatusChange}
                                                className="holiday-filter-select"
                                            >
                                                <option value="">{t('Status')}</option>
                                                <option value="1">{t('UNPAID')}</option>
                                                <option value="2">{t('PAID')}</option>
                                            </select>
                                            <div className="holiday-filter-icon">
                                                {/* Chevron down icon */}
                                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                    <path d="M5 8l5 5 5-5" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                    <button className="holiday-filter-clear-btn"
                                        onClick={handleClearFilters}
                                    >
                                        {t('CLEAR')}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="invoice-table-content">
                            {/* Loading State */}

                            {/* Table */}
                            {!loading && !error && paginatedHolidayRecords && paginatedHolidayRecords.length >= 0 && (
                                <div className="holiday-pay-table-scroll-wrapper">
                                    <div className="invoice-table">
                                        {!isMobile && (
                                            <TableHeader>
                                                <TableColumn>ID</TableColumn>
                                                <TableColumn>{t('TOTAL_AMOUNT_REQUESTED')}</TableColumn>
                                                <TableColumn>{t('ADMINISTRATOR_FEE')}</TableColumn>
                                                <TableColumn>{t('AMOUNT')}</TableColumn>
                                                <TableColumn>{t('TAX_AMOUNT')}</TableColumn>
                                                <TableColumn>{t('TO_PAY_AMOUNT')}</TableColumn>
                                                <TableColumn>Status</TableColumn>
                                                <TableColumn>{t('PAYOUT_DATE')}</TableColumn>
                                            </TableHeader>
                                        )}

                                        <TableBody>
                                            {loading && (
                                                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                                                    {t('LOADING')}
                                                </div>
                                            )}
                                            {paginatedHolidayRecords.length === 0 && (
                                                <div className="invoice-table">
                                                    <div className="invoice-table-content">
                                                        {/* No drafts found message */}
                                                        {(paginatedHolidayRecords.length === 0) && (
                                                            <div className='d-flex justify-content-center flex-column align-items-center pt-5'>
                                                                <span className='pt-3'>{t('NO_HISTORY_RECORDS_FOUND')}.</span>
                                                                {error}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            {isMobile ? (
                                                // Mobile: Card-based layout
                                                paginatedHolidayRecords?.map((record: HolidayRecord) => (
                                                    <MobileCardWrapper key={record.id || Math.random()}>
                                                        <MobileCardRow label={t('ID') || 'Customer Name'}>
                                                            <span className="invoice-table-detailed__invoice-link">{record.id || 'N/A'}</span>
                                                        </MobileCardRow>
                                                        <MobileCardRow label={t('AMOUNT_REQUESTED')}>
                                                            {formatAmount(record.totalAmountRequested) ?? 'N/A'}
                                                        </MobileCardRow>
                                                        <MobileCardRow label={t('ADMIN_FEE')}>
                                                            {formatAmount(record.administratorFee) ?? 'N/A'}
                                                        </MobileCardRow>
                                                        <MobileCardRow label={t('AMOUNT')}>
                                                            {formatAmount(record.amount) ?? 'N/A'}
                                                        </MobileCardRow>
                                                        <MobileCardRow label={t('TAX_AMOUNT')}>
                                                            {formatAmount(record.taxAmount) ?? 'N/A'}
                                                        </MobileCardRow>
                                                        <MobileCardRow label={t('TO_PAY_AMOUNT')}>
                                                            {formatAmount(record.amountToPay) ?? 'N/A'}
                                                        </MobileCardRow>
                                                        <MobileCardRow label='Status'>
                                                            <span className={getStatusBadgeClass(record.status)}>
                                                                {record.status === 1 ? t('UNPAID') : record.status === 2 ? t('PAID') : 'N/A'}
                                                            </span>
                                                        </MobileCardRow>
                                                        <MobileCardRow label={t('PAYOUT_DATE')}>
                                                            {record?.status === 2 && record?.paidDate
                                                                ? formatDateForPayout(record.paidDate, false, language)
                                                                : 'N/A'}
                                                        </MobileCardRow>
                                                    </MobileCardWrapper>
                                                ))
                                            ) : (
                                                paginatedHolidayRecords.map((record: HolidayRecord) => (
                                                    <TableRow key={record.id || Math.random()}>
                                                        <TableCell>
                                                            <span className="invoice-table-detailed__invoice-link">{record.id || 'N/A'}</span>
                                                        </TableCell>
                                                        <TableCell>{formatAmount(record.totalAmountRequested) ?? 'N/A'}</TableCell>
                                                        <TableCell>{formatAmount(record.administratorFee) ?? 'N/A'}</TableCell>
                                                        <TableCell>{formatAmount(record.amount) ?? 'N/A'}</TableCell>
                                                        <TableCell>{formatAmount(record.taxAmount) ?? 'N/A'}</TableCell>
                                                        <TableCell>{formatAmount(record.amountToPay) ?? 'N/A'}</TableCell>
                                                        <TableCell>
                                                            <span className={getStatusBadgeClass(record.status)}>
                                                                {record.status === 1 ? t('UNPAID') : record.status === 2 ? t('PAID') : 'N/A'}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>
                                                            {record?.status === 2 && record?.paidDate
                                                                ? formatDateForPayout(record.paidDate, false, language)
                                                                : 'N/A'}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </div>
                                </div>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && paginatedHolidayRecords && paginatedHolidayRecords.length > 0 && (
                                <div className="invoice-table-pagination">
                                    <div className="pagination-controls">
                                        <button
                                            className="pagination-button pagination-button--prev"
                                            onClick={goToPreviousPage}
                                            disabled={currentPage === 1}
                                        >
                                            <ArrowLeftIcon />
                                            <span>{t('PREV_STEP') || 'Prev'}</span>
                                        </button>

                                        {getPageNumbers().map((page, index) => {
                                            if (page === '...') {
                                                return (
                                                    <button
                                                        key={`ellipsis-${index}`}
                                                        className="pagination-button pagination-button--number"
                                                        disabled
                                                    >
                                                        ...
                                                    </button>
                                                );
                                            }
                                            return (
                                                <button
                                                    key={page}
                                                    className={`pagination-button pagination-button--number${page === currentPage ? ' pagination-button--active' : ''
                                                        }`}
                                                    onClick={() => setCurrentPage(page as number)}
                                                >
                                                    {page}
                                                </button>
                                            );
                                        })}

                                        <button
                                            className="pagination-button pagination-button--next"
                                            onClick={goToNextPage}
                                            disabled={currentPage === totalPages}
                                        >
                                            <span>{t('NEXT_STEP') || 'Next'}</span>
                                            <ArrowRightIcon />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {activeTab === 'transaction-history-label' && (
                    <HolidayInvoiceHistoryModal />
                )}

            </div>
            <HolidayPayoutModal
                isOpen={showPayoutModal}
                onClose={() => setShowPayoutModal(false)}
                onConfirm={payoutAmount}
                maxAmount={calculatedBalance?.response?.holidaySavingsAmount || 0}
                currency="SEK"
                loading={payoutLoading}
            />
        </div>
    );
}